import { AD_STYLES_18, AD_ANGLES_18 } from '../../../../shared/constants/adStyles';
import { adStyleOf, AD_STYLE_OPTIONS } from './ad-style';

/** Match stored niche tags without requiring customers to distinguish casing. */
export const nicheKey = (value: string) => value.trim().toLowerCase();

export function availableNiches(templates: ReadonlyArray<{ niche?: string[] | null }>): string[] {
  const names = new Map<string, string>();
  for (const template of templates) {
    for (const name of template.niche ?? []) {
      const key = nicheKey(name);
      if (key && !names.has(key)) names.set(key, name.trim());
    }
  }
  return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
}

export function nicheLabel(value: string): string {
  const labels: Record<string, string> = { saas: 'SaaS', b2b: 'B2B', cbd: 'CBD', ugc: 'UGC' };
  return labels[nicheKey(value)] ?? value.replace(/[-_]+/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

export function matchesNiche(niches: string[] | null | undefined, selected: string[]): boolean {
  return !selected.length || (niches ?? []).some(value => selected.some(filter => nicheKey(value) === nicheKey(filter)));
}

export type InspirationRecord = {
  name: string;
  sourceAdStyle?: string | null;
  sourceAdAngle?: string | null;
  adStyle?: string | null;
  adAngle?: string | null;
};
export type InspirationSelection = { value: string; axis?: 'style' | 'angle' };
const hasField = (record: InspirationRecord, field: keyof InspirationRecord) => Object.prototype.hasOwnProperty.call(record, field);

/** Stored source classifications take precedence, including explicit nulls. */
export function inspirationMetadata(record: InspirationRecord): { style: string; angle: string } {
  const style = hasField(record, 'sourceAdStyle') ? record.sourceAdStyle : hasField(record, 'adStyle') ? record.adStyle : adStyleOf(record);
  const angle = hasField(record, 'sourceAdAngle') ? record.sourceAdAngle : record.adAngle;
  return { style: style?.trim() ?? '', angle: angle?.trim() ?? '' };
}

/** Keep the full taxonomy available even when the current collection is empty. */
export function availableInspiration(records: ReadonlyArray<InspirationRecord>): string[] {
  const labels = new Map<string, string>();
  const add = (label: string) => {
    const key = nicheKey(label);
    if (key && !labels.has(key)) labels.set(key, label.trim());
  };
  [...AD_STYLES_18, ...AD_ANGLES_18, ...AD_STYLE_OPTIONS].forEach(add);
  records.forEach(record => { const { style, angle } = inspirationMetadata(record); add(style); add(angle); });
  return [...labels.values()].sort((a, b) => a.localeCompare(b));
}

/** Labels match either axis; legacy style handoffs retain their narrower intent. */
export function matchesInspiration(record: InspirationRecord, selected: ReadonlyArray<InspirationSelection>): boolean {
  if (!selected.length) return true;
  const metadata = inspirationMetadata(record);
  return selected.some(({ value, axis }) => {
    const key = nicheKey(value);
    return !!key && (axis ? nicheKey(metadata[axis]) === key : nicheKey(metadata.style) === key || nicheKey(metadata.angle) === key);
  });
}
